import { calendarDaysBetween, toLocalDate } from '@pazportal/ui';

/** Format cents to currency string like $8,400.00 */
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/** Format file size in bytes to human-readable */
export function formatFileSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Format ISO date string to relative time ("2 hours ago") */
export function timeAgo(dateStr: string): string {
  const date = toLocalDate(dateStr);
  if (!date) return '—';
  const now = Date.now();
  const then = date.getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * A bare elapsed duration: "45m", "5h", "3d".
 *
 * Separate from `timeAgo` because that one bakes in the English word "ago",
 * which cannot be embedded in a translated sentence — the Spanish form puts
 * "hace" before the number, so the duration has to arrive on its own. Unit
 * letters are read the same in both bundles (h/m/d: horas, minutos, días).
 */
export function formatDuration(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? ms : 0;
  const minutes = Math.floor(safe / 60_000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Whole calendar days from `dateA` to `dateB` (positive when B is later).
 *
 * Both sides are local calendar days: a DATE such as a contract renewal is not
 * UTC midnight, and "now" counts as today rather than a fraction of one.
 */
export function daysBetween(dateA: string | Date, dateB: string | Date): number {
  return calendarDaysBetween(dateA, dateB) ?? NaN;
}

/** Format solution_type enum to human label */
export function formatSolutionType(type: string): string {
  const labels: Record<string, string> = {
    managed_network: 'Managed Network Solutions',
    video_surveillance: 'Surveillance Solutions',
    access_control: 'Access Control',
    pro_av: 'Pro AV',
    digital_signage: 'Digital Signage',
    security_alarm: 'Security / Alarm',
    smart_robotics: 'Smart Robotics Solutions',
    keytracer: 'Security Solutions (KeyTracer)',
    it_cloud: 'IT & Cloud',
    pos_systems: 'Retail Solutions (POS)',
    conferencing: 'Conferencing Solutions',
    audio: 'Audio Solutions',
  };
  return labels[type] ?? type;
}

/** Format audit action to human-readable string */
export function formatAuditAction(action: string): string {
  const parts = action.split('.');
  if (parts.length === 2) {
    const table = parts[0].replace(/_/g, ' ');
    const verb = parts[1].toLowerCase();
    const verbMap: Record<string, string> = {
      insert: 'created',
      update: 'updated',
      delete: 'deleted',
    };
    return `${table} ${verbMap[verb] ?? verb}`;
  }
  return action.replace(/_/g, ' ');
}

/**
 * Format date to short display.
 *
 * A bare `YYYY-MM-DD` (a Postgres DATE: visit dates, due dates, renewals) is a
 * calendar day, so it is read as local midnight; `new Date('2025-10-01')`
 * would be UTC midnight and show as Sep 30 across the US. Timestamps keep
 * their instant.
 */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = toLocalDate(dateStr);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Format date with time */
export function formatDateTime(dateStr: string | null): string {
  const date = toLocalDate(dateStr);
  if (!date) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
